/**
 * HustleSuite Terms of Use — mirror partner to the privacy policy.
 * Rendered at /legal/terms. Kept intentionally simple so App Review
 * doesn't stumble over it.
 */
import React from "react";
import { Link } from "react-router-dom";

const container = {
  maxWidth: 780,
  margin: "0 auto",
  padding: "64px 24px 96px",
  fontFamily: "-apple-system, Segoe UI, Roboto, Arial, sans-serif",
  color: "#111827",
  lineHeight: 1.65,
  fontSize: 16,
};

const h1 = { fontSize: 32, margin: "0 0 8px", fontWeight: 700 };
const h2 = { fontSize: 20, margin: "36px 0 8px", fontWeight: 600 };
const meta = { color: "#6b7280", fontSize: 14, marginBottom: 32 };
const p = { margin: "0 0 14px" };
const ul = { margin: "0 0 14px 20px", padding: 0 };

export default function HustleSuiteTermsPage() {
  return (
    <div style={container} data-testid="hustlesuite-terms-page">
      <h1 style={h1}>HustleSuite Terms of Use</h1>
      <p style={meta}>Last updated: 12 February 2026</p>

      <p style={p}>
        These Terms govern your use of HustleSuite (the app, the website,
        and all modules operating under it). By creating an account or
        using any HustleSuite service, you agree to these Terms. If you
        don't agree, don't use HustleSuite.
      </p>

      <h2 style={h2}>1. Who we are</h2>
      <p style={p}>
        HustleSuite is operated by <strong>LOCAL HUSTLE</strong>
        (ABN&nbsp;16&nbsp;160&nbsp;565&nbsp;775), a registered business
        in Queensland, Australia.
      </p>

      <h2 style={h2}>2. Your account</h2>
      <ul style={ul}>
        <li>You must be 13 or older to create an account</li>
        <li>You are responsible for keeping your password confidential</li>
        <li>You are responsible for all activity that occurs under your account</li>
        <li>You must provide accurate information</li>
        <li>You may delete your account at any time from your account settings</li>
      </ul>

      <h2 style={h2}>3. Acceptable use</h2>
      <p style={p}>You must not:</p>
      <ul style={ul}>
        <li>Post unlawful, abusive, threatening, or defamatory content</li>
        <li>Impersonate another person or entity</li>
        <li>Interfere with the security or operation of the service</li>
        <li>Attempt to gain unauthorised access to any account, system, or data</li>
        <li>Scrape, mine, or crawl the service without written permission</li>
        <li>Use the service to send spam or unsolicited commercial communications</li>
      </ul>

      <h2 style={h2}>4. User-generated content</h2>
      <p style={p}>
        You retain all rights to content you post. You grant us a
        non-exclusive, royalty-free licence to display and distribute your
        content within HustleSuite for the purpose of operating the
        service. We may remove content that violates these Terms.
      </p>
      <p style={p}>
        We use human review and automated moderation to enforce these
        Terms. You can report objectionable content via the report link
        available on every user-generated item. We aim to review reports
        within 24 hours and remove content that violates our policies.
      </p>

      <h2 style={h2}>5. Purchases and payments</h2>
      <ul style={ul}>
        <li>Payments are processed by Stripe under Stripe's terms</li>
        <li>Prices are shown in Australian Dollars (AUD) unless otherwise stated</li>
        <li>Refunds are handled by the individual seller or module operator per Australian Consumer Law</li>
        <li>You are responsible for any duties, taxes, or shipping fees stated at checkout</li>
      </ul>

      <h2 style={h2}>6. Intellectual property</h2>
      <p style={p}>
        The HustleSuite name, logo, and platform code are our property.
        Third-party trademarks displayed within listings belong to their
        respective owners.
      </p>

      <h2 style={h2}>7. Service availability</h2>
      <p style={p}>
        HustleSuite is provided "as is". We work hard to keep it running,
        but we don't guarantee uninterrupted service. We reserve the right
        to change, suspend, or discontinue any part of the service at any
        time.
      </p>

      <h2 style={h2}>8. Limitation of liability</h2>
      <p style={p}>
        To the extent permitted by law, we are not liable for indirect,
        incidental, special, consequential, or punitive damages arising
        from your use of the service. Nothing in these Terms limits any
        consumer guarantee or right that cannot be lawfully excluded under
        Australian Consumer Law.
      </p>

      <h2 style={h2}>9. Termination</h2>
      <p style={p}>
        We may suspend or terminate your account if you breach these Terms
        or engage in behaviour that harms other users. You may terminate
        your account at any time.
      </p>

      <h2 style={h2}>10. Changes to these Terms</h2>
      <p style={p}>
        We may update these Terms from time to time. Material changes
        will be notified via email or in-app banner at least 14 days
        before they take effect.
      </p>

      <h2 style={h2}>11. Governing law</h2>
      <p style={p}>
        These Terms are governed by the laws of Queensland, Australia.
      </p>

      <h2 style={h2}>12. Contact</h2>
      <p style={p}>
        For any legal enquiry:{" "}
        <a href="mailto:legal@hustlesuite.au">legal@hustlesuite.au</a>
      </p>

      <p style={{ ...meta, marginTop: 48 }}>
        See also our{" "}
        <Link to="/legal/privacy" style={{ color: "#c55a48" }}>
          Privacy Policy
        </Link>
        .
      </p>
    </div>
  );
}
